/**
 * useWearable — owns the active-wearable integration query and the connect / disconnect /
 * sync handlers.
 *
 * The menu-event window listeners stay in the container; the container forwards a
 * menu-triggered sync into `setPendingMenuSync(true)`, which this hook's effect consumes.
 */
import { useState, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/keys';
import {
  listIntegrations,
  createIntegration,
  authorizeIntegration,
  deleteIntegration,
} from '@/lib/api/endpoints/integrations';
import { http } from '@/lib/http';
import { useToast } from '@/app/providers/ToastContext';
import { HEALTH_STATS_QUERY_KEY } from './useHealthData';

const HEALTH_PROVIDERS = ['oura', 'whoop', 'strava', 'fitbit'] as const;

interface UseWearableArgs {
  token: string | null | undefined;
  conversationId: string;
  moduleId: string;
}

export function useWearable({ token, conversationId, moduleId }: UseWearableArgs) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [connecting, setConnecting] = useState<string | false>(false);
  const [syncing, setSyncing] = useState(false);

  const { data: activeWearable } = useQuery({
    queryKey: ['integrations', 'health-wearable'],
    queryFn: async () => {
      const integrations = await listIntegrations(token ?? null);
      for (const provider of HEALTH_PROVIDERS) {
        const found = integrations.find((i: any) => i.provider === provider && i.status === 'active');
        if (found) return { ...found, provider } as any;
      }
      return null;
    },
    enabled: Boolean(token),
  });

  const handleConnectProvider = useCallback(async (provider: string, label: string) => {
    if (!token) return;
    setConnecting(provider);
    try {
      const integration = await createIntegration(token, { provider, name: `My ${label}` });
      const authUrl = await authorizeIntegration(token, integration.id, window.location.href);
      window.open(authUrl, '_blank', 'noopener');
      // Re-check wearable status when user returns from OAuth tab
      const onFocus = () => {
        queryClient.invalidateQueries({ queryKey: ['integrations', 'health-wearable'] });
        window.removeEventListener('focus', onFocus);
      };
      window.addEventListener('focus', onFocus);
    } catch (err) {
      console.error(`Failed to connect ${label}:`, err);
      showToast(`Failed to connect ${label}. Please try again.`, 'error');
    }
    finally { setConnecting(false); }
  }, [token, showToast, queryClient]);

  const handleDisconnectWearable = useCallback(async () => {
    if (!token || !activeWearable) return;
    try {
      await deleteIntegration(token, activeWearable.id);
      queryClient.invalidateQueries({ queryKey: ['integrations', 'health-wearable'] });
      showToast('Wearable disconnected.', 'success');
    } catch (err) {
      console.error('Failed to disconnect wearable:', err);
      showToast('Failed to disconnect wearable.', 'error');
    }
  }, [token, activeWearable, queryClient, showToast]);

  const handleSyncWearable = useCallback(async () => {
    if (!token || !activeWearable) return;
    setSyncing(true);
    try {
      const integrationId = activeWearable.id;
      if (activeWearable.provider === 'whoop') {
        await http.post(`/integrations/${integrationId}/whoop-sync`, { days: 14 }, { authToken: token });
      } else {
        await http.post(`/integrations/${integrationId}/sync`, {
          ...(conversationId ? { conversationId } : {}),
          module_id: moduleId,
        }, { authToken: token });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('stat') });
      queryClient.invalidateQueries({ queryKey: HEALTH_STATS_QUERY_KEY });
      showToast('Sync complete', 'success');
    } catch (err: any) {
      const message = err?.body?.error?.message || err?.message || '';
      console.error('Health sync failed:', err);
      if (message.includes('rate limit') || message.includes('wait')) {
        showToast('Please wait 30 seconds between syncs.', 'info');
      } else {
        showToast('Sync failed. Please try again.', 'error');
      }
    }
    finally { setSyncing(false); }
  }, [token, activeWearable, conversationId, moduleId, queryClient, showToast]);

  // Track whether a menu-triggered sync is pending (set by container's listener, consumed here)
  const [pendingMenuSync, setPendingMenuSync] = useState(false);

  // Trigger sync when requested via menu event
  useEffect(() => {
    if (pendingMenuSync) {
      setPendingMenuSync(false);
      handleSyncWearable();
    }
  }, [pendingMenuSync, handleSyncWearable]);

  return {
    activeWearable,
    connecting,
    syncing,
    handleConnectProvider,
    handleDisconnectWearable,
    handleSyncWearable,
    setPendingMenuSync,
  };
}
